import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus, List, Columns, Search, Trash2, RotateCcw, Download,
  ChevronRight, Archive, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../api/client';
import type { Event, EventStatus, BulkResult } from '../../api/types';
import { EventStatusBadge } from '../../components/ui/Badge';
import { EmptyState } from '../../components/ui/EmptyState';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { BulkActionBar } from '../../components/ui/BulkActionBar';
import { ConfirmDialog } from '../../components/ui/Modal';
import { useTableSelection } from '../../hooks/useTableSelection';
import { formatDate, formatCurrency, formatEventType, clientFullName } from '../../utils/format';
import { useDebounce } from '../../hooks/useDebounce';
import NewEventModal from './NewEventModal';
import { PageHeader, type ActionDef } from '../../components/ui/PageHeader';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function eventDisplayAmount(ev: Event): number {
  if (Number(ev.totalAmount) > 0) return Number(ev.totalAmount);
  if (!ev.services?.length) return 0;
  return ev.services.reduce((sum, s) => {
    const p = Number(s.unitPrice);
    if (s.discountType === 'FIXED' && s.discount != null) return sum + Math.max(0, p - Number(s.discount));
    if (s.discountType === 'PERCENTAGE' && s.discount != null) return sum + p * (1 - Number(s.discount) / 100);
    return sum + p;
  }, 0);
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUSES: EventStatus[] = ['PREVENTIVO','CONTRATTO','CONFERMATO','IN_LAVORAZIONE','CONSEGNATO','ARCHIVIATO'];
const STATUS_LABELS: Record<EventStatus, string> = {
  PREVENTIVO: 'Preventivo', CONTRATTO: 'Contratto', CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione', CONSEGNATO: 'Consegnato', ARCHIVIATO: 'Archiviato',
};
const STATUS_COLORS: Record<EventStatus, string> = {
  PREVENTIVO: 'var(--color-text-faint)', CONTRATTO: 'var(--color-primary)',
  CONFERMATO: 'var(--color-success)', IN_LAVORAZIONE: 'var(--color-warning)',
  CONSEGNATO: 'var(--color-gold)', ARCHIVIATO: 'var(--color-text-faint)',
};

// ─── Sort ─────────────────────────────────────────────────────────────────────

type SortField = 'title' | 'eventDate' | 'client' | 'amount' | 'status';
type SortOrder = 'asc' | 'desc';

function sortEvents(events: Event[], field: SortField, order: SortOrder): Event[] {
  return [...events].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'title':     cmp = a.title.localeCompare(b.title); break;
      case 'eventDate': cmp = new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime(); break;
      case 'client':    cmp = clientFullName(a.client).localeCompare(clientFullName(b.client)); break;
      case 'amount':    cmp = eventDisplayAmount(a) - eventDisplayAmount(b); break;
      case 'status':    cmp = (STATUSES.indexOf(a.status as EventStatus) - STATUSES.indexOf(b.status as EventStatus)); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

// ─── Icon helper ─────────────────────────────────────────────────────────────

const EVENT_TYPE_DOT_COLORS: Record<string, string> = {
  MATRIMONIO: '#e91e63', BATTESIMO: '#2196f3', COMUNIONE: '#ff9800',
  CRESIMA: '#f44336', COMPLEANNO: '#9c27b0', RITRATTO: '#4caf50', ALTRO: '#757575',
};

function EventTypeIcon({ type, size = 16, style }: { type: string; size?: number; style?: React.CSSProperties }) {
  return <div style={{ width: size, height: size, borderRadius: '50%', background: EVENT_TYPE_DOT_COLORS[type] || EVENT_TYPE_DOT_COLORS.ALTRO, flexShrink: 0, ...style }} />;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EventsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchRaw, setSearchRaw] = useState('');
  const search = useDebounce(searchRaw, 300);
  const [showNew, setShowNew] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState<SortField>('eventDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Bulk confirm dialogs
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkArchiveConfirm, setShowBulkArchiveConfirm] = useState(false);
  const [showBulkRestoreConfirm, setShowBulkRestoreConfirm] = useState(false);

  const resetKey = `${filterStatus}|${filterType}|${search}|${showDeleted}`;
  const sel = useTableSelection(resetKey);

  const { data = [] as Event[], isLoading } = useQuery({
    queryKey: ['events', filterStatus, filterType, search, showDeleted],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '200', sort: 'eventDate', order: 'asc' });
      if (filterStatus) p.set('status', filterStatus);
      if (filterType) p.set('eventType', filterType);
      if (search) p.set('search', search);
      if (showDeleted) p.set('includeDeleted', 'true');
      const res = await apiGet(`/events?${p}`);
      return ((res as any)?.data ?? res) as Event[];
    },
  });

  const allEvents = data;
  const events = showDeleted
    ? allEvents!.filter(e => e.deletedAt)
    : allEvents!.filter(e => !e.deletedAt);

  const sortedEvents = useMemo(
    () => (!showDeleted ? sortEvents(events, sortBy, sortOrder) : events),
    [events, sortBy, sortOrder, showDeleted],
  );

  const handleSort = (field: string) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field as SortField); setSortOrder('asc'); }
  };

  // Mutations
  const restoreMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/events/${id}/restore`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['events'] }); toast.success('Evento ripristinato'); },
    onError: () => toast.error('Errore durante il ripristino'),
  });

  const cloneMutation = useMutation({
    mutationFn: (id: string) => apiPost<{ id: string }>(`/events/${id}/clone`),
    onSuccess: (ev) => { queryClient.invalidateQueries({ queryKey: ['events'] }); toast.success('Evento clonato'); navigate(`/events/${ev.id}`); },
    onError: () => toast.error('Errore durante la clonazione'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/events/bulk/delete', { ids }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['events'] }); sel.clearSelection(); setShowBulkDeleteConfirm(false); toast.success(`${res.updated} eventi eliminati`); },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/events/bulk/archive', { ids }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['events'] }); sel.clearSelection(); setShowBulkArchiveConfirm(false); toast.success(`${res.updated} eventi archiviati`); },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkRestoreMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/events/bulk/restore', { ids }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['events'] }); sel.clearSelection(); setShowBulkRestoreConfirm(false); toast.success(`${res.updated} eventi ripristinati`); },
    onError: (e: Error) => toast.error(e.message),
  });

  const selectedIds = Array.from(sel.selectedIds);

  const csvHref = () => {
    const p = new URLSearchParams();
    if (filterStatus) p.set('status', filterStatus);
    if (filterType) p.set('eventType', filterType);
    if (search) p.set('search', search);
    if (sel.selectedCount > 0) p.set('ids', selectedIds.join(','));
    return `/api/events/export/csv?${p}`;
  };

  const listColumns: ColumnDef<Event>[] = [
    { key: 'icon', label: '', render: (e) => <EventTypeIcon type={e.eventType} size={16} style={{ marginTop: 2 }} /> },
    { key: 'title', label: 'Evento', sortable: true, render: (e) => (
      <Link to={`/events/${e.id}`} onClick={(ev) => ev.stopPropagation()} style={{ minWidth: 0, textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.client ? clientFullName(e.client) : '—'}</div>
      </Link>
    )},
    { key: 'eventDate', label: 'Data', sortable: true, render: (e) => <span style={{ whiteSpace: 'nowrap' }}>{formatDate(e.eventDate, 'd MMM yyyy')}</span> },
    { key: 'status', label: 'Stato', sortable: true, render: (e) => <EventStatusBadge status={e.status} size="sm" /> },
    { key: 'type', label: 'Tipo', render: (e) => <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{formatEventType(e.eventType)}</span> },
    { key: 'revenue', label: 'Ricavo', type: 'currency', render: (e) => <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{formatCurrency(eventDisplayAmount(e))}</span> },
    { key: 'actions', label: '', render: (e) => (
      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }} onClick={(ev) => ev.stopPropagation()}>
        <button onClick={() => navigate(`/events/${e.id}`)} title="Apri" aria-label="Apri evento" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--color-text-muted)', borderRadius: 'var(--radius-interactive)', display: 'flex' }}>
          <ChevronRight size={14} />
        </button>
        <button onClick={() => cloneMutation.mutate(e.id)} disabled={cloneMutation.isPending} title="Clona" aria-label="Clona evento" style={{ background: 'none', border: 'none', cursor: cloneMutation.isPending ? 'not-allowed' : 'pointer', padding: '0.25rem', color: 'var(--color-text-muted)', borderRadius: 'var(--radius-interactive)', display: 'flex', opacity: cloneMutation.isPending ? 0.5 : 1 }}>
          <Copy size={14} />
        </button>
      </div>
    )},
  ];

  const trashColumns: ColumnDef<Event>[] = [
    { key: 'icon', label: '', render: (e) => <EventTypeIcon type={e.eventType} size={16} style={{ marginTop: 2 }} /> },
    { key: 'title', label: 'Evento', render: (e) => (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatEventType(e.eventType)}</div>
      </div>
    )},
    { key: 'date', label: 'Data', render: (e) => <span style={{ whiteSpace: 'nowrap' }}>{formatDate(e.eventDate, 'd MMM yyyy')}</span> },
    { key: 'client', label: 'Cliente', render: (e) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{e.client ? clientFullName(e.client) : '—'}</span> },
    { key: 'deletedAt', label: 'Eliminato il', render: (e) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{e.deletedAt ? formatDate(e.deletedAt, 'd MMM yyyy') : '—'}</span> },
    { key: 'actions', label: '', render: (e) => (
      <div onClick={(ev) => ev.stopPropagation()}>
        <button onClick={() => restoreMutation.mutate(e.id)} disabled={restoreMutation.isPending} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '0.3rem 0.6rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', cursor: restoreMutation.isPending ? 'not-allowed' : 'pointer' }}>
          <RotateCcw size={12} /> Ripristina
        </button>
      </div>
    )},
  ];

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        title={showDeleted ? 'Cestino' : 'Eventi'}
        subtitle={`${events.length} ${showDeleted ? 'eventi eliminati' : 'eventi'}`}
        actions={[
          { label: 'Lista', icon: <List size={14} />, onClick: () => setView('list'), variant: (view === 'list' ? 'primary' : 'ghost') as ActionDef['variant'] },
          { label: 'Kanban', icon: <Columns size={14} />, onClick: () => setView('kanban'), variant: (view === 'kanban' ? 'primary' : 'ghost') as ActionDef['variant'] },
          { label: showDeleted ? 'Eventi' : 'Cestino', icon: <Trash2 size={14} />, onClick: () => setShowDeleted(v => !v), variant: (showDeleted ? 'primary' : 'ghost') as ActionDef['variant'] },
          { label: 'CSV', icon: <Download size={14} />, onClick: () => { window.location.href = csvHref(); } },
          { label: 'Nuovo evento', icon: <Plus size={16} />, onClick: () => setShowNew(true), variant: 'primary' as ActionDef['variant'] },
        ]}
      />

      {/* Filters */}
      <div style={{ display: showDeleted ? 'none' : 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
          <input
            placeholder="Cerca eventi…"
            value={searchRaw}
            onChange={e => setSearchRaw(e.target.value)}
            style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', padding: '0.4375rem 0.75rem 0.4375rem 2.25rem', width: '100%', outline: 'none' }}
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: filterStatus ? 'var(--color-text)' : 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.4375rem 0.875rem', cursor: 'pointer', outline: 'none' }}>
          <option value="">Tutti gli stati</option>
          {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: filterType ? 'var(--color-text)' : 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.4375rem 0.875rem', cursor: 'pointer', outline: 'none' }}>
          <option value="">Tutti i tipi</option>
          {['MATRIMONIO','BATTESIMO','COMUNIONE','CRESIMA','COMPLEANNO','RITRATTO','ALTRO'].map(t => (
            <option key={t} value={t}>{formatEventType(t)}</option>
          ))}
        </select>
      </div>

      {/* Bulk action bar */}
      {!showDeleted && (
        <BulkActionBar
          count={sel.selectedCount}
          onClear={sel.clearSelection}
          actions={[
            { label: 'Archivia', icon: <Archive size={13} />, variant: 'secondary', onClick: () => setShowBulkArchiveConfirm(true), loading: bulkArchiveMutation.isPending },
            { label: 'Elimina', icon: <Trash2 size={13} />, variant: 'danger', onClick: () => setShowBulkDeleteConfirm(true), loading: bulkDeleteMutation.isPending },
          ]}
        />
      )}
      {showDeleted && (
        <BulkActionBar
          count={sel.selectedCount}
          onClear={sel.clearSelection}
          actions={[
            { label: 'Ripristina', icon: <RotateCcw size={13} />, variant: 'secondary', onClick: () => setShowBulkRestoreConfirm(true), loading: bulkRestoreMutation.isPending },
          ]}
        />
      )}

      {/* Views */}
      {showDeleted ? (
        <div className="table-responsive">
          <DataTable
            columns={trashColumns}
            data={sortedEvents}
            gridColumns="36px 2fr 1fr 1fr 1fr 130px"
            rowKey={(e) => e.id}
            emptyState={<EmptyState icon={<Trash2 size={32} />} title="Cestino vuoto" description="Non ci sono eventi nel cestino" />}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            selectable={!showDeleted}
            selectedIds={sel.selectedIds}
            onSelectionChange={(ids) => sel.setSelectedIds(ids)}
            isLoading={isLoading}
            stickyHeader
          />
        </div>
      ) : view === 'kanban' ? (
        <KanbanView events={sortedEvents} isLoading={isLoading} onEventClick={id => navigate(`/events/${id}`)} />
      ) : (
        <div className="table-responsive">
          <DataTable
            columns={listColumns}
            data={sortedEvents}
            gridColumns="36px 2fr 1fr 1fr 0.8fr 100px 44px"
            rowKey={(e) => e.id}
            onRowClick={(e) => navigate(`/events/${e.id}`)}
            emptyState={<EmptyState icon={<Search size={32} />} title="Nessun evento trovato" description="Modifica i filtri o crea un nuovo evento" />}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            selectable={!showDeleted}
            selectedIds={sel.selectedIds}
            onSelectionChange={(ids) => sel.setSelectedIds(ids)}
            isLoading={isLoading}
            stickyHeader
          />
        </div>
      )}

      <NewEventModal open={showNew} onClose={() => setShowNew(false)} />

      {/* Bulk confirms */}
      <ConfirmDialog
        open={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedIds)}
        loading={bulkDeleteMutation.isPending}
        requiresConfirmCheck
        title={`Eliminare ${sel.selectedCount} eventi?`}
        description="Gli eventi verranno spostati nel cestino. Potrai ripristinarli in seguito."
        confirmLabel="Elimina"
      />
      <ConfirmDialog
        open={showBulkArchiveConfirm}
        onClose={() => setShowBulkArchiveConfirm(false)}
        onConfirm={() => bulkArchiveMutation.mutate(selectedIds)}
        loading={bulkArchiveMutation.isPending}
        title={`Archiviare ${sel.selectedCount} eventi?`}
        description="Gli eventi selezionati verranno impostati come ARCHIVIATO."
        confirmLabel="Archivia"
      />
      <ConfirmDialog
        open={showBulkRestoreConfirm}
        onClose={() => setShowBulkRestoreConfirm(false)}
        onConfirm={() => bulkRestoreMutation.mutate(selectedIds)}
        loading={bulkRestoreMutation.isPending}
        title={`Ripristinare ${sel.selectedCount} eventi?`}
        description="Gli eventi selezionati verranno rimossi dal cestino."
        confirmLabel="Ripristina"
      />
    </div>
  );
}

// ─── KanbanView ───────────────────────────────────────────────────────────────

function KanbanView({ events, isLoading, onEventClick }: { events: Event[]; isLoading: boolean; onEventClick: (id: string) => void }) {
  const columns: EventStatus[] = ['PREVENTIVO','CONTRATTO','CONFERMATO','IN_LAVORAZIONE','CONSEGNATO','ARCHIVIATO'];
  return (
    <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
      {columns.map(status => {
        const colEvents = events.filter(e => e.status === status);
        return (
          <div key={status} style={{ minWidth: 240, background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: STATUS_COLORS[status] }}>{STATUS_LABELS[status]}</span>
              <span style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-full)', padding: '0 0.375rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{colEvents.length}</span>
            </div>
            <div style={{ flex: 1, padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 80 }}>
              {colEvents.length === 0 && !isLoading && (
                <div style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>Nessun evento</div>
              )}
              {colEvents.map(ev => (
                <button key={ev.id} onClick={() => onEventClick(ev.id)}
                  style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', background: 'var(--glass-bg-card)', border: '1px solid transparent', borderRadius: 'var(--radius-card)', padding: '0.75rem', cursor: 'pointer', textAlign: 'left', transition: 'all var(--transition-interactive)', width: '100%' }}
                  onMouseEnter={e => { (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'); }}
                  onMouseLeave={e => { (e.currentTarget.style.borderColor = 'transparent'); }}>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)' }}>{ev.title}</p>
                  {ev.client && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{clientFullName(ev.client)}</p>}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{formatDate(ev.eventDate)}</span>
                    <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-gold)' }}>{formatCurrency(eventDisplayAmount(ev))}</span>
                  </div>
                  {ev._count && <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.125rem' }}><span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{ev._count.services} servizi</span></div>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
